import { BarsArrowDownIcon, BarsArrowUpIcon, ClockIcon } from '@heroicons/react/24/outline'
import { UserInfo } from 'pages/ManageAccounts'
import { useEffect, useState } from 'react'
import { filterUsers } from 'services'
import { svgSearch } from 'stories/assets'
import { Input, Modal, Pagination } from 'stories/components'

import { SubscribeLogModal } from './SubscribeLogModal'

const itemCountPerPage = 10

export const SubscribeUsersModal = ({ onClose }: { onClose: Function }) => {
  const [loading, setLoading] = useState(false)
  const [users, setUsers] = useState<Array<UserInfo>>([])
  const [total, setTotal] = useState(0)
  const [filters, setFilters] = useState<Record<string, any>>({
    query: '',
    status: 'active',
    accountType: '0',
    pullCreditScoreLevel: '',
    accountExecutive: '',
    orderBy: 'name',
    orderDir: '1',
    pageNum: 0,
    showOthers: false,
    showDeleted: false,
    showLoanSharing: false,
    loanActivity: false,
    subscribeBroadcastEmail: true,
  })
  const [filterQuery, setFilterQuery] = useState(filters.query)
  const [isGetUsersOnce, setIsGetUsersOnce] = useState(false)
  const [showModal, setShowModal] = useState(false)

  useEffect(() => {
    if (!isGetUsersOnce) return
    const timeOutId = setTimeout(() => !loading && onChangeFilter('pageNum', 0), 700)
    return () => clearTimeout(timeOutId)
  }, [filterQuery])

  useEffect(() => {
    filterUsersData(filters).then(() => {
      setIsGetUsersOnce(true)
    })
  }, [])

  const filterUsersData = async (filters: Record<string, any>) => {
    setLoading(true)
    const { data, total } = await filterUsers({
      query: filters.query.trim(),
      status: filters.status,
      accountType: filters.accountType == '0' ? undefined : filters.accountType,
      pullCreditScoreLevel: filters.pullCreditScoreLevel ? filters.pullCreditScoreLevel : undefined,
      accountExecutive: filters.accountExecutive,
      count: itemCountPerPage,
      skip: filters.pageNum * itemCountPerPage,
      orderBy: filters.orderBy,
      orderDir: filters.orderDir,
      showDeleted: filters.showDeleted,
      showLoanSharing: filters.showLoanSharing,
      showOthers: filters.showOthers,
      subscribeBroadcastEmail: filters.subscribeBroadcastEmail,
    })
    setUsers(data)
    setTotal(total)
    setLoading(false)
  }

  const onChangeFilter = (key: string, value: any) => {
    const newFilters = Object.assign({}, filters)
    newFilters[key] = value
    setFilters(newFilters)
    if (key === 'query') setFilterQuery(value)
    else filterUsersData(newFilters)
  }

  const onPageNavigate = (num: number) => {
    onChangeFilter('pageNum', num)
  }

  const renderHeader = (title: any, sortable: boolean = false, key: string, sortOrder: number = 1) => {
    if (!sortable)
      return (
        <th scope="col" className="py-3" key={key}>
          {title}
        </th>
      )

    const onSort = () => {
      if (sortOrder == 0) sortOrder = -1
      const newFilters = Object.assign({}, filters)
      newFilters['orderBy'] = key
      newFilters['orderDir'] = `${0 - sortOrder}`
      setFilters(newFilters)
      filterUsersData(newFilters)
    }

    return (
      <th scope="col" className="py-3" key={key}>
        <button className="flex uppercase bg-transparent font-bold" onClick={() => onSort()}>
          {title}
          {sortOrder !== 0 ? (
            sortOrder == 1 ? (
              <BarsArrowUpIcon className="w-3 h-3 ml-2" />
            ) : (
              <BarsArrowDownIcon className="w-3 h-3 ml-2" />
            )
          ) : (
            <div className="w-3 h-3 ml-2" />
          )}
        </button>
      </th>
    )
  }

  const sortableHeaders = [
    { title: 'Company Name', key: 'companyName' },
    { title: 'Name', key: 'name' },
    { title: 'Email', key: 'email' },
  ]

  return (
    <Modal title="Subscribed Users" titleOkay="" loading={loading} onClose={() => onClose()} isOpen>
      <>
        <div className="max-w-[300px]">
          <Input
            type="search"
            title="Search Accounts"
            hasIcon
            icon={svgSearch}
            value={filters.query}
            onChange={(value) => onChangeFilter('query', value)}
          />
        </div>
        <table className="w-full text-sm text-left text-gray-900 dark:text-gray-400 pl-6">
          <thead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide bg-slate-50 border-b border-slate-200 dark:bg-gray-700 dark:text-gray-400">
            <tr>
              <th scope="col" className="pl-6 py-3 pr-3">
                No
              </th>
              {sortableHeaders.map(({ title, key }) => {
                return renderHeader(title, true, key, filters.orderBy == key ? parseInt(filters.orderDir) : 0)
              })}
            </tr>
          </thead>
          {users.map((user, index) => {
            return (
              <tr
                className={`${
                  index % 2 ? 'bg-gray-50' : ''
                } bg-white hover:bg-gray-100 border-b dark:bg-gray-800 dark:border-gray-700`}
                key={`${index}`}
              >
                <td scope="row" className="pl-6 py-3 pr-3 font-medium text-gray-900 dark:text-white whitespace-nowrap">
                  {filters.pageNum * itemCountPerPage + index + 1}
                </td>
                <td className="pr-3">{user.companyName}</td>
                <td className="pr-3">{user.name}</td>
                <td className="pr-3">{user.email}</td>
              </tr>
            )
          })}
        </table>
        <div className="flex justify-end items-center mt-3 mb-3">
          <Pagination
            totalCount={total}
            itemCountPerPage={itemCountPerPage}
            onNavigate={onPageNavigate}
            pageNum={filters.pageNum}
          />
          <span onClick={() => setShowModal(true)}>
            <ClockIcon className="h-5 w-5 cursor-pointer text-primary" aria-hidden="true" />
          </span>
        </div>
        {showModal && <SubscribeLogModal onClose={() => setShowModal(false)} />}
      </>
    </Modal>
  )
}
